import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Send, Plus, Trash2, Check } from 'lucide-react';

const TOTAL_STEPS = 9;

const SOCIAL_PLATFORMS = [
    { id: 'facebook', label: 'Facebook' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'threads', label: 'Threads' },
    { id: 'linkedin', label: 'LinkedIn' },
    { id: 'sonstiges', label: 'Sonstiges' }
];

const OnboardingQuestionnaire = ({ onComplete, onSkip, logoUrl, companyName }) => {
    const { api } = useAuth();
    const [step, setStep] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [data, setData] = useState({
        branches: '',
        strengths: '',
        weaknesses: '',
        work_values: '',
        learn_goals: '',
        social_media: [],
        favorite_profiles: [{ link: '', reason: '' }],
        hobbies: ''
    });

    const canProceed = () => {
        switch (step) {
            case 1: return data.branches.trim().length > 0;
            case 2: return data.strengths.trim().length > 0;
            case 3: return data.weaknesses.trim().length > 0;
            case 4: return data.work_values.trim().length > 0;
            case 5: return data.learn_goals.trim().length > 0;
            case 6: return data.social_media.length > 0;
            case 7: return data.favorite_profiles.some(p => p.link.trim() && p.reason.trim()) && data.favorite_profiles.filter(p => p.link.trim() && p.reason.trim()).length >= 3;
            case 8: return data.hobbies.trim().length > 0;
            case 9: return true;
            default: return false;
        }
    };

    const toggleSocial = (id) => {
        setData(prev => ({
            ...prev,
            social_media: prev.social_media.includes(id)
                ? prev.social_media.filter(s => s !== id)
                : [...prev.social_media, id]
        }));
    };

    const updateProfile = (index, field, value) => {
        setData(prev => {
            const profiles = [...prev.favorite_profiles];
            profiles[index] = { ...profiles[index], [field]: value };
            return { ...prev, favorite_profiles: profiles };
        });
    };

    const addProfile = () => {
        if (data.favorite_profiles.length < 5) {
            setData(prev => ({ ...prev, favorite_profiles: [...prev.favorite_profiles, { link: '', reason: '' }] }));
        }
    };

    const removeProfile = (index) => {
        if (data.favorite_profiles.length > 1) {
            setData(prev => ({ ...prev, favorite_profiles: prev.favorite_profiles.filter((_, i) => i !== index) }));
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const submitData = {
                ...data,
                favorite_profiles: data.favorite_profiles.filter(p => p.link.trim())
            };
            await api.post('/questionnaire', submitData);
            toast.success('Fragebogen erfolgreich abgesendet!');
            onComplete();
        } catch {
            toast.error('Fehler beim Absenden');
        } finally {
            setSubmitting(false);
        }
    };

    const stepLabels = [
        'Branchen', 'Stärken', 'Schwächen', 'Arbeit', 'Lernen', 'Social Media', 'Profile', 'Hobbys', 'Zusammenfassung'
    ];

    const renderStep = () => {
        const variants = {
            enter: { opacity: 0, x: 40 },
            center: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -40 }
        };

        switch (step) {
            case 1:
                return (
                    <motion.div key="s1" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Mit welchen Themen / Branchen kennst du dich aus?</h2>
                        <p className="text-muted-foreground">z.B. Gastronomie, B2B, Kosmetik, E-Commerce...</p>
                        <Textarea
                            value={data.branches}
                            onChange={e => setData({ ...data, branches: e.target.value })}
                            placeholder="Beschreibe deine Branchenkenntnisse..."
                            className="min-h-[120px] rounded-xl text-base"
                            data-testid="q-branches"
                            autoFocus
                        />
                    </motion.div>
                );
            case 2:
                return (
                    <motion.div key="s2" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Was sind deine Stärken?</h2>
                        <p className="text-muted-foreground">z.B. Team Koordination, Grafiken Erstellen, Texte schreiben...</p>
                        <Textarea
                            value={data.strengths}
                            onChange={e => setData({ ...data, strengths: e.target.value })}
                            placeholder="Beschreibe deine Stärken..."
                            className="min-h-[120px] rounded-xl text-base"
                            data-testid="q-strengths"
                            autoFocus
                        />
                    </motion.div>
                );
            case 3:
                return (
                    <motion.div key="s3" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Was sind deine Schwächen?</h2>
                        <p className="text-muted-foreground">Sei ehrlich - das hilft uns, dich besser zu unterstützen.</p>
                        <Textarea
                            value={data.weaknesses}
                            onChange={e => setData({ ...data, weaknesses: e.target.value })}
                            placeholder="Beschreibe deine Schwächen..."
                            className="min-h-[120px] rounded-xl text-base"
                            data-testid="q-weaknesses"
                            autoFocus
                        />
                    </motion.div>
                );
            case 4:
                return (
                    <motion.div key="s4" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Was ist dir wichtig bei der Arbeit?</h2>
                        <p className="text-muted-foreground">Was motiviert dich? Worauf legst du Wert?</p>
                        <Textarea
                            value={data.work_values}
                            onChange={e => setData({ ...data, work_values: e.target.value })}
                            placeholder="Was ist dir bei der Arbeit wichtig..."
                            className="min-h-[120px] rounded-xl text-base"
                            data-testid="q-work-values"
                            autoFocus
                        />
                    </motion.div>
                );
            case 5:
                return (
                    <motion.div key="s5" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Was würdest du gerne können / lernen?</h2>
                        <p className="text-muted-foreground">Welche Skills möchtest du entwickeln?</p>
                        <Textarea
                            value={data.learn_goals}
                            onChange={e => setData({ ...data, learn_goals: e.target.value })}
                            placeholder="Was möchtest du lernen..."
                            className="min-h-[120px] rounded-xl text-base"
                            data-testid="q-learn-goals"
                            autoFocus
                        />
                    </motion.div>
                );
            case 6:
                return (
                    <motion.div key="s6" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Auf welchen Social Media Plattformen hältst du dich auf?</h2>
                        <p className="text-muted-foreground">Wähle alle Plattformen aus, die du nutzt.</p>
                        <div className="grid grid-cols-2 gap-3 mt-4">
                            {SOCIAL_PLATFORMS.map(p => (
                                <button
                                    key={p.id}
                                    onClick={() => toggleSocial(p.id)}
                                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                                        data.social_media.includes(p.id)
                                            ? 'border-primary bg-primary/5'
                                            : 'border-border hover:border-muted-foreground/30'
                                    }`}
                                    data-testid={`q-social-${p.id}`}
                                >
                                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                                        data.social_media.includes(p.id) ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                                    }`}>
                                        {data.social_media.includes(p.id) && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <span className="font-medium">{p.label}</span>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                );
            case 7:
                return (
                    <motion.div key="s7" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">3-5 Profile mit gutem Auftritt</h2>
                        <p className="text-muted-foreground">Nenne uns 3-5 Seiten (Link zum Profil), die deiner Meinung nach einen guten Auftritt haben und schreib in kurzen Sätzen warum.</p>
                        <div className="space-y-4 mt-2">
                            {data.favorite_profiles.map((profile, i) => (
                                <div key={i} className="p-4 rounded-xl border border-border bg-muted/20 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-muted-foreground">Profil {i + 1}</span>
                                        {data.favorite_profiles.length > 1 && (
                                            <Button variant="ghost" size="sm" onClick={() => removeProfile(i)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                    <Input
                                        value={profile.link}
                                        onChange={e => updateProfile(i, 'link', e.target.value)}
                                        placeholder="https://instagram.com/beispiel"
                                        className="rounded-xl"
                                        data-testid={`q-profile-link-${i}`}
                                    />
                                    <Textarea
                                        value={profile.reason}
                                        onChange={e => updateProfile(i, 'reason', e.target.value)}
                                        placeholder="Warum findest du den Auftritt gut?"
                                        className="min-h-[60px] rounded-xl text-sm"
                                        data-testid={`q-profile-reason-${i}`}
                                    />
                                </div>
                            ))}
                        </div>
                        {data.favorite_profiles.length < 5 && (
                            <Button variant="outline" onClick={addProfile} className="rounded-xl gap-2 w-full" data-testid="q-add-profile">
                                <Plus className="w-4 h-4" />
                                Weiteres Profil hinzufügen
                            </Button>
                        )}
                        {data.favorite_profiles.filter(p => p.link.trim() && p.reason.trim()).length < 3 && (
                            <p className="text-sm text-amber-500">Mindestens 3 Profile mit Link und Begründung erforderlich.</p>
                        )}
                    </motion.div>
                );
            case 8:
                return (
                    <motion.div key="s8" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-4">
                        <h2 className="text-2xl font-bold">Welche Hobbys hast du?</h2>
                        <p className="text-muted-foreground">Was machst du gerne in deiner Freizeit?</p>
                        <Textarea
                            value={data.hobbies}
                            onChange={e => setData({ ...data, hobbies: e.target.value })}
                            placeholder="Erzähl uns von deinen Hobbys..."
                            className="min-h-[120px] rounded-xl text-base"
                            data-testid="q-hobbies"
                            autoFocus
                        />
                    </motion.div>
                );
            case 9:
                return (
                    <motion.div key="s9" variants={variants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25 }} className="space-y-5">
                        <h2 className="text-2xl font-bold">Zusammenfassung</h2>
                        <p className="text-muted-foreground">Prüfe deine Antworten bevor du absendest.</p>
                        <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                            {[
                                { label: 'Branchen', value: data.branches },
                                { label: 'Stärken', value: data.strengths },
                                { label: 'Schwächen', value: data.weaknesses },
                                { label: 'Wichtig bei der Arbeit', value: data.work_values },
                                { label: 'Lernen / Können', value: data.learn_goals },
                                { label: 'Social Media', value: data.social_media.join(', ') },
                                { label: 'Hobbys', value: data.hobbies }
                            ].map((item, i) => (
                                <div key={i} className="p-3 rounded-xl bg-muted/30 border border-border/40">
                                    <p className="text-xs font-medium text-muted-foreground mb-1">{item.label}</p>
                                    <p className="text-sm">{item.value}</p>
                                </div>
                            ))}
                            <div className="p-3 rounded-xl bg-muted/30 border border-border/40">
                                <p className="text-xs font-medium text-muted-foreground mb-2">Gute Profile ({data.favorite_profiles.filter(p => p.link.trim()).length})</p>
                                {data.favorite_profiles.filter(p => p.link.trim()).map((p, i) => (
                                    <div key={i} className="mb-2 last:mb-0">
                                        <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline break-all">{p.link}</a>
                                        <p className="text-xs text-muted-foreground mt-0.5">{p.reason}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-background flex flex-col" data-testid="onboarding-questionnaire">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                    {logoUrl && <img src={logoUrl} alt="Logo" className="h-8 object-contain" />}
                    <span className="text-lg font-bold">{companyName || 'AgencyOS'}</span>
                </div>
                <span className="text-sm text-muted-foreground">{step} / {TOTAL_STEPS}</span>
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground hover:text-foreground"
                    onClick={onSkip}
                    data-testid="q-skip"
                >
                    Beim nächsten Login ausfüllen
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-y-auto">
                <div className="w-full max-w-xl py-8">
                    <AnimatePresence mode="wait">
                        {renderStep()}
                    </AnimatePresence>
                </div>
            </div>

            {/* Footer: Navigation + Progress */}
            <div className="border-t border-border/50 px-6 py-4">
                {/* Progress Timeline */}
                <div className="flex items-center justify-center gap-1 mb-4">
                    {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                        <div key={i} className="flex items-center">
                            <button
                                onClick={() => { if (i + 1 < step) setStep(i + 1); }}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                                    i + 1 === step
                                        ? 'bg-primary text-white scale-110'
                                        : i + 1 < step
                                        ? 'bg-primary/20 text-primary cursor-pointer hover:bg-primary/30'
                                        : 'bg-muted text-muted-foreground'
                                }`}
                                disabled={i + 1 > step}
                            >
                                {i + 1 < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                            </button>
                            {i < TOTAL_STEPS - 1 && (
                                <div className={`w-4 sm:w-6 h-0.5 ${i + 1 < step ? 'bg-primary/30' : 'bg-muted'}`} />
                            )}
                        </div>
                    ))}
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-between max-w-xl mx-auto">
                    <Button
                        variant="outline"
                        onClick={() => setStep(s => Math.max(1, s - 1))}
                        disabled={step === 1}
                        className="rounded-xl gap-2"
                        data-testid="q-back"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Zurück
                    </Button>

                    {step < TOTAL_STEPS ? (
                        <Button
                            onClick={() => setStep(s => s + 1)}
                            disabled={!canProceed()}
                            className="rounded-xl gap-2"
                            data-testid="q-next"
                        >
                            Weiter
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="rounded-xl gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                            data-testid="q-submit"
                        >
                            {submitting ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Absenden
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OnboardingQuestionnaire;
